/**
 * Parseo editorial de noticias etiquetadas (Epígrafe / Titular / Bajada / Cuerpo)
 * y de pares de noticias para comparación en impresión.
 */

export interface StructuredNewspaperArticle {
  outlet?: string;
  kicker?: string;
  headline: string;
  deck?: string;
  body: string;
  raw: string;
}

export interface NewsContrastRow {
  label: string;
  left: string;
  right: string;
}

export interface NewsComparisonBundle {
  articles: [StructuredNewspaperArticle, StructuredNewspaperArticle];
  matrix: NewsContrastRow[];
  reflection?: string;
}

const FIELD_ALIASES: Record<'kicker' | 'headline' | 'deck' | 'body', RegExp> = {
  kicker: /^(?:ep[ií]grafe|volanta|kicker|antet[ií]tulo)\s*:\s*/im,
  headline: /^(?:titular|headline|t[ií]tulo)\s*:\s*/im,
  deck: /^(?:bajada|lead|entradilla|subt[ií]tulo|deck)\s*:\s*/im,
  body: /^(?:cuerpo|body|desarrollo|texto)\s*:\s*/im,
};

const ANY_FIELD_LABEL_RE =
  /\b(?:ep[ií]grafe|volanta|kicker|antet[ií]tulo|titular|headline|t[ií]tulo|bajada|lead|entradilla|subt[ií]tulo|deck|cuerpo|body|desarrollo)\s*:/gi;

const INLINE_FIELD_RE =
  /\b(Ep[ií]grafe|Volanta|Kicker|Titular|Headline|T[ií]tulo|Bajada|Lead|Entradilla|Subt[ií]tulo|Cuerpo|Body|Desarrollo)\s*:\s*/gi;

const ARTICLE_SPLIT_RE =
  /(?:\*\*)?\s*Noticia\s*([12])\s*(?:\(([^)]+)\))?\s*:?\s*(?:\*\*)?\s*/gi;

const REFLECTION_RE = /(?:\*\*)?Reflexi[oó]n(?:\*\*)?\s*:?\s*(.+)$/is;

function stripMarkdownNoise(text: string): string {
  return String(text || '')
    .replace(/\*\*/g, '')
    .replace(/^#+\s+/gm, '')
    .replace(/\r\n/g, '\n')
    .trim();
}

function fieldHitCount(text: string): number {
  const blob = String(text || '');
  const matches = blob.match(ANY_FIELD_LABEL_RE);
  return matches ? new Set(matches.map((m) => m.toLowerCase().replace(/\s*:$/, ''))).size : 0;
}

function hasLabeledNewsFields(text: string): boolean {
  return fieldHitCount(text) >= 2;
}

function lineHasMultipleFields(line: string): boolean {
  return fieldHitCount(line) >= 2;
}

function assignField(
  article: Partial<StructuredNewspaperArticle>,
  label: string,
  value: string,
): void {
  const v = value.trim();
  if (!v) return;
  if (/ep[ií]grafe|volanta|kicker|antet[ií]tulo/i.test(label)) article.kicker = v;
  else if (/titular|headline|^t[ií]tulo$/i.test(label)) article.headline = v;
  else if (/bajada|lead|entradilla|subt[ií]tulo|deck/i.test(label)) article.deck = v;
  else if (/cuerpo|body|desarrollo|texto/i.test(label)) article.body = v;
}

/** Extrae campos etiquetados de un bloque de noticia (una sola pieza). */
export function parseStructuredNewspaperArticle(
  text: string,
  outlet?: string,
): StructuredNewspaperArticle | null {
  const raw = String(text || '').trim();
  if (!raw) return null;

  const cleaned = stripMarkdownNoise(raw);
  if (!hasLabeledNewsFields(cleaned) && !hasLabeledNewsFields(raw)) return null;

  const source = hasLabeledNewsFields(cleaned) ? cleaned : raw;
  const article: Partial<StructuredNewspaperArticle> = { outlet, raw };

  const lines = source.split('\n').map((l) => l.trim()).filter(Boolean);
  const useInline = lines.some(lineHasMultipleFields) || lines.length <= 1;

  if (!useInline) {
    let current: keyof typeof FIELD_ALIASES | null = null;
    const buckets: Record<keyof typeof FIELD_ALIASES, string[]> = {
      kicker: [],
      headline: [],
      deck: [],
      body: [],
    };

    for (const line of lines) {
      let matched = false;
      for (const [key, re] of Object.entries(FIELD_ALIASES) as Array<
        [keyof typeof FIELD_ALIASES, RegExp]
      >) {
        if (re.test(line)) {
          current = key;
          buckets[key].push(line.replace(re, '').trim());
          matched = true;
          break;
        }
      }
      if (!matched && current) {
        buckets[current].push(line);
      }
    }

    if (buckets.kicker.length) article.kicker = buckets.kicker.join(' ').trim();
    if (buckets.headline.length) article.headline = buckets.headline.join(' ').trim();
    if (buckets.deck.length) article.deck = buckets.deck.join(' ').trim();
    if (buckets.body.length) article.body = buckets.body.join('\n').trim();
  } else {
    // Inline: "Epígrafe: … Titular: … Bajada: … Cuerpo: …"
    const markers: Array<{ index: number; length: number }> = [];
    const re = new RegExp(INLINE_FIELD_RE.source, 'gi');
    let m: RegExpExecArray | null;
    while ((m = re.exec(source)) !== null) {
      markers.push({ index: m.index, length: m[0].length });
    }
    for (let i = 0; i < markers.length; i += 1) {
      const start = markers[i]!.index + markers[i]!.length;
      const end = i + 1 < markers.length ? markers[i + 1]!.index : source.length;
      const labelMatch = source
        .slice(markers[i]!.index, start)
        .match(
          /(Ep[ií]grafe|Volanta|Kicker|Titular|Headline|T[ií]tulo|Bajada|Lead|Entradilla|Subt[ií]tulo|Cuerpo|Body|Desarrollo)\s*:/i,
        );
      if (!labelMatch) continue;
      assignField(article, labelMatch[1]!, source.slice(start, end));
    }
  }

  if (!article.headline && !article.body) return null;

  const headline =
    article.headline?.trim() ||
    article.deck?.trim() ||
    article.kicker?.trim() ||
    'Noticia';
  const body =
    article.body?.trim() ||
    [article.deck, article.kicker].filter(Boolean).join('\n\n').trim() ||
    headline;

  return {
    outlet: article.outlet?.trim() || undefined,
    kicker: article.kicker?.trim() || undefined,
    headline,
    deck: article.deck?.trim() || undefined,
    body,
    raw,
  };
}

function defaultMatrix(
  left: StructuredNewspaperArticle,
  right: StructuredNewspaperArticle,
): NewsContrastRow[] {
  const rows: NewsContrastRow[] = [];
  const push = (label: string, a?: string, b?: string) => {
    if (!a && !b) return;
    rows.push({ label, left: a || '—', right: b || '—' });
  };
  push('Medio', left.outlet, right.outlet);
  push('Epígrafe', left.kicker, right.kicker);
  push('Titular', left.headline, right.headline);
  push('Bajada / Lead', left.deck, right.deck);
  return rows;
}

function parseReflection(text: string): { body: string; reflection?: string } {
  const match = text.match(REFLECTION_RE);
  if (!match) return { body: text };
  return {
    body: text.slice(0, match.index).trim(),
    reflection: match[1]?.replace(/\*\*/g, '').trim(),
  };
}

export interface MixedNewsComparisonExtract {
  instruction?: string;
  bundle: NewsComparisonBundle;
  trailing?: string;
}

function findFirstArticleHeaders(text: string): {
  first1: { outlet?: string; start: number; headerLen: number };
  first2: { outlet?: string; start: number; headerLen: number };
} | null {
  const re = new RegExp(ARTICLE_SPLIT_RE.source, 'gi');
  let first1: { outlet?: string; start: number; headerLen: number } | null = null;
  let first2: { outlet?: string; start: number; headerLen: number } | null = null;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    const n = Number(m[1]);
    const hit = {
      outlet: m[2]?.trim(),
      start: m.index,
      headerLen: m[0].length,
    };
    if (n === 1 && !first1) first1 = hit;
    if (n === 2 && !first2) first2 = hit;
    if (first1 && first2) break;
  }
  if (!first1 || !first2) return null;
  return { first1, first2 };
}

function cutArticleBody(source: string, contentStart: number, hardEnd: number): {
  content: string;
  cutAt: number;
} {
  const slice = source.slice(contentStart, hardEnd);
  // Corta basura tipo "**Noticia 1** ----- Técnico Creativo…" o tablas colapsadas
  const junk = slice.search(
    /(?:^|\n)\s*(?:\*\*)?Noticia\s*[12](?:\*\*)?\s*(?:-{3,}|_{3,}|\|)/i,
  );
  const dashRun = slice.search(/(?:^|\n)\s*-{8,}\s*/);
  let cut = slice.length;
  if (junk >= 0) cut = Math.min(cut, junk);
  if (dashRun >= 0) cut = Math.min(cut, dashRun);
  return { content: slice.slice(0, cut).trim(), cutAt: contentStart + cut };
}

/**
 * Extrae comparación Noticia 1/2 aunque venga consigna delante
 * ("Lee estas dos noticias…") o basura tabular después.
 */
export function extractNewsComparisonFromMixedText(
  text: string,
): MixedNewsComparisonExtract | null {
  const raw = String(text || '').trim();
  if (!raw) return null;

  const { body: withoutReflection, reflection } = parseReflection(raw);
  const headers = findFirstArticleHeaders(withoutReflection);
  if (!headers) return null;

  const { first1, first2 } = headers;
  const h1 = first1;
  const h2 = first2;
  if (h1.start > h2.start) return null;

  const instruction = withoutReflection.slice(0, h1.start).replace(/[:：]\s*$/, '').trim();

  const leftCut = cutArticleBody(
    withoutReflection,
    h1.start + h1.headerLen,
    h2.start,
  );
  const rightCut = cutArticleBody(
    withoutReflection,
    h2.start + h2.headerLen,
    withoutReflection.length,
  );

  const left = parseStructuredNewspaperArticle(leftCut.content, h1.outlet);
  const right = parseStructuredNewspaperArticle(rightCut.content, h2.outlet);
  if (!left || !right) return null;

  const trailing = withoutReflection.slice(rightCut.cutAt).trim();
  const cleanedTrailing = trailing
    .replace(/^(?:\*\*)?Noticia\s*[12](?:\*\*)?\s*/i, '')
    .replace(/^-{3,}\s*/gm, '')
    .trim();

  return {
    instruction: instruction || undefined,
    bundle: {
      articles: [left, right],
      matrix: defaultMatrix(left, right),
      reflection,
    },
    trailing: cleanedTrailing || undefined,
  };
}

/** Detecta dos noticias etiquetadas Noticia 1 / Noticia 2 en el mismo texto. */
export function parseNewsComparisonBundle(text: string): NewsComparisonBundle | null {
  return extractNewsComparisonFromMixedText(text)?.bundle ?? null;
}

/** True si el texto parece una muestra periodística etiquetada (no teoría). */
export function looksLikeStructuredNewspaperSample(text: string): boolean {
  if (parseNewsComparisonBundle(text)) return true;
  return parseStructuredNewspaperArticle(text) != null;
}

/** Glosario / pares cortos → filas de contraste editorial. */
export function entriesToContrastRows(
  entries: Array<{ term?: string; definition?: string }>,
): NewsContrastRow[] {
  return entries
    .map((e, index) => {
      const term = String(e.term || '').trim();
      const definition = String(e.definition || '').trim();
      if (!term && !definition) return null;
      const split = definition.split(/\s*[/|–—]\s*/).map((s) => s.trim()).filter(Boolean);
      if (split.length === 2) {
        return { label: term || `Aspecto ${index + 1}`, left: split[0]!, right: split[1]! };
      }
      // Par término ↔ definición (p. ej. Técnico | Creativo en columnas)
      return {
        label: term || `Aspecto ${index + 1}`,
        left: term || '—',
        right: definition || '—',
      };
    })
    .filter((row): row is NewsContrastRow => Boolean(row));
}
